<template>
    <div class="d-flex flex-fill flex-column px-2 pb-10">
        <fe-grid
            ref="grid"
            :key="gridKey"
            class="incident-grid"
            style="height: 100%"
            :columnDefs="columns"
            :title="showHeader ? title : ''"
            :rowData="showAllIncidentCodes ? undefined : gridItems"
            :showToolbar="showHeader"
            :showAddRowBtn="false"
            :detailCellRendererParams="showAllIncidentCodes ? null : detailCellRendererParams"
            :detailRowHeight="showAllIncidentCodes ? null : detailRowHeight"
            :gridOptionOverrides="gridOptions"
            :csvExportProcessCellCallback="csvExportProcessCellCallback"
            :csvExportProcessCallback="handleExport"
            :masterDetail="!showAllIncidentCodes"
            :bordered="false"
            showScrollbars
            role="grid"
            :aria-label="title || 'Student Incidents'"
            @rowDoubleClicked="loadIncident"
            @gridReady="onGridReady"
            @rowDataChanged="updateIncidentCount"
            @filterChanged="updateIncidentCount"
            @search="onServerSearch"
            @searchColumn="onServerSearch"
            @sortChanged="onSortChanged"
        >
            <template v-if="totalCount !== null" #after-search-toolbar-items>
                <div class="incident-count-display" aria-live="polite">
                    <span class="incident-count-text">{{ totalCount.toLocaleString() }} incident {{ totalCount === 1 ? 'event' : 'events' }} by student</span>
                </div>
            </template>
            <template v-if="showAddRowBtn" v-slot:toolbar-items>
                <fe-btn @click="createIncident">New</fe-btn>
            </template>
        </fe-grid>
    </div>
</template>

<script>
    import Vuex, {mapState} from 'vuex'
    import IFrameRenderer from '@/components/common/IFrameRenderer'
    import dayjs from 'dayjs'
    import lodash from 'lodash'

    const DESCRIPTION_TRUNCATION_THRESHOLD = 40

    const popoverMixin = {
        data() {
            return {
                showPopover: false,
                popoverEl: null,
                _clickOutsideHandler: null,
                _scrollHandler: null
            }
        },
        mounted() {
            this._clickOutsideHandler = (e) => {
                if (this.showPopover && this.popoverEl && !this.popoverEl.contains(e.target) && !this.$el.contains(e.target)) {
                    this.closePopover()
                }
            }
            this._scrollHandler = () => {
                if (this.showPopover) {
                    this.closePopover()
                }
            }
            document.addEventListener('click', this._clickOutsideHandler)
        },
        beforeDestroy() {
            this.closePopover()
            if (this._clickOutsideHandler) {
                document.removeEventListener('click', this._clickOutsideHandler)
            }
        },
        methods: {
            togglePopover(event) {
                if (event) {
                    event.preventDefault()
                    event.stopPropagation()
                }
                if (this.showPopover) {
                    this.closePopover()
                } else {
                    this.openPopover()
                }
            },
            createPopover(contentEl) {
                this.showPopover = true

                const popover = document.createElement('div')
                popover.className = 'ec-description-popover'
                popover.setAttribute('role', 'dialog')
                popover.setAttribute('aria-label', this.popoverAriaLabel || 'Details')
                popover.addEventListener('click', (e) => e.stopPropagation())

                popover.appendChild(contentEl)

                const closeLink = document.createElement('span')
                closeLink.className = 'ec-description-popover-close'
                closeLink.textContent = 'Show less'
                closeLink.setAttribute('role', 'button')
                closeLink.setAttribute('tabindex', '0')
                closeLink.addEventListener('click', (e) => {
                    e.stopPropagation()
                    this.closePopover()
                })
                closeLink.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        this.closePopover()
                    }
                })
                popover.appendChild(closeLink)

                document.body.appendChild(popover)
                this.popoverEl = popover

                this._positionPopover(popover)

                const gridViewport = this.$el.closest('.ag-body-viewport')
                if (gridViewport) {
                    gridViewport.addEventListener('scroll', this._scrollHandler)
                }

                closeLink.focus()
            },
            _positionPopover(popover) {
                const cellRect = this.$el.getBoundingClientRect()
                popover.style.left = cellRect.left + 'px'
                popover.style.top = cellRect.top + 'px'
                popover.style.minWidth = cellRect.width + 'px'

                requestAnimationFrame(() => {
                    if (!this.popoverEl) return
                    const rect = popover.getBoundingClientRect()
                    if (rect.right > window.innerWidth) {
                        popover.style.left = Math.max(0, window.innerWidth - rect.width - 8) + 'px'
                    }
                    if (rect.bottom > window.innerHeight) {
                        const above = cellRect.top - rect.height
                        popover.style.top = (above >= 0 ? above : 0) + 'px'
                    }
                })
            },
            closePopover() {
                this.showPopover = false
                if (this.popoverEl) {
                    const gridViewport = this.$el?.closest('.ag-body-viewport')
                    if (gridViewport) {
                        gridViewport.removeEventListener('scroll', this._scrollHandler)
                    }
                    this.popoverEl.remove()
                    this.popoverEl = null
                }
            }
        }
    }

    let cellIcon = {
        template: "<span v-if='!params.node.expanded' style='cursor: pointer'><i class='fe-grid-icon fas fa-chevron-down'></i></span>\n" +
            "<span v-else style='cursor: pointer'><i class='fe-grid-icon fas fa-chevron-up'></i></span>"
    }

    let descriptionCell = {
        mixins: [popoverMixin],
        template: `
            <div :id="'desc-' + (params.data?.id || params.node?.id || 'unknown')" class="description-cell-wrapper" aria-live="polite" aria-atomic="true">
                <span ref="descriptionText" class="description-text">{{ truncatedDescription }}</span>
                <span v-if="showButton" class="show-more-link" @click.stop="togglePopover" aria-label="Show full description" data-test="resize-description-btn" role="button" tabindex="0" @keydown.enter.stop="togglePopover" @keydown.space.prevent.stop="togglePopover">Show more</span>
            </div>
        `,
        computed: {
            popoverAriaLabel() {
                return 'Full description'
            },
            description() {
                return this.params.value || ''
            },
            truncatedDescription() {
                if (this.description.length > DESCRIPTION_TRUNCATION_THRESHOLD) {
                    return this.description.substring(0, DESCRIPTION_TRUNCATION_THRESHOLD) + '...'
                }
                return this.description
            },
            isTruncated() {
                return this.description.length > DESCRIPTION_TRUNCATION_THRESHOLD
            },
            showButton() {
                return this.isTruncated && !this.showPopover
            }
        },
        methods: {
            openPopover() {
                const content = document.createElement('div')
                content.className = 'ec-description-popover-content'
                content.textContent = this.description
                this.createPopover(content)
            }
        }
    }

    let listPopoverCell = {
        mixins: [popoverMixin],
        template: `
            <div class="incident-list-cell-wrapper">
                <div class="incident-cell-item">
                    <span class="incident-cell-text">{{ primaryName }}</span>
                    <span v-if="primaryFlag" class="incident-primary-badge">Primary</span>
                </div>
                <span v-if="hasMore" class="show-more-link" @click.stop="togglePopover" :aria-label="showMoreAriaLabel" :data-test="showMoreDataTest" role="button" tabindex="0" @keydown.enter.stop="togglePopover" @keydown.space.prevent.stop="togglePopover">Show more</span>
            </div>
        `,
        computed: {
            popoverAriaLabel() {
                return this.params.colDef.field === 'incident_types_codes'
                    ? 'All incident types and behaviors'
                    : 'All responses'
            },
            showMoreAriaLabel() {
                return this.params.colDef.field === 'incident_types_codes'
                    ? 'Show all incident types and behaviors'
                    : 'Show all responses'
            },
            showMoreDataTest() {
                return this.params.colDef.field === 'incident_types_codes'
                    ? 'show-more-behaviors-btn'
                    : 'show-more-responses-btn'
            },
            items() {
                if (!this.params.data) return []
                const field = this.params.colDef.field
                if (field === 'incident_types_codes') {
                    return (this.params.data._behaviorsArray || []).map(b => ({
                        name: b.display_name,
                        primary: b.primary_flag
                    }))
                }
                if (field === 'responses') {
                    return (this.params.data._responsesArray || []).map(r => ({
                        name: r.incident_response_name,
                        primary: r.primary_flag
                    }))
                }
                return []
            },
            primaryItem() {
                return this.items.find(i => i.primary) || this.items[0] || null
            },
            primaryName() {
                if (this.primaryItem) return this.primaryItem.name
                return this.params.value || ''
            },
            primaryFlag() {
                return this.primaryItem?.primary || false
            },
            hasMore() {
                return this.items.length > 1 && !this.showPopover
            }
        },
        methods: {
            openPopover() {
                const list = document.createElement('div')
                list.className = 'ec-description-popover-content'
                this.items.forEach(item => {
                    const row = document.createElement('div')
                    row.style.display = 'flex'
                    row.style.alignItems = 'center'
                    row.style.gap = '6px'
                    row.style.marginBottom = '6px'

                    const nameSpan = document.createElement('span')
                    nameSpan.textContent = item.name
                    row.appendChild(nameSpan)

                    if (item.primary) {
                        const badge = document.createElement('span')
                        badge.className = 'incident-primary-badge'
                        badge.textContent = 'Primary'
                        row.appendChild(badge)
                    }
                    list.appendChild(row)
                })
                this.createPopover(list)
            }
        }
    }

    export default {
        name: 'IncidentGrid',

        components: {
            IFrameRenderer,
            cellIcon,
            descriptionCell,
            listPopoverCell,
        },

        props: {
            params: {
                required: true
            },
            title: {
                type: String,
                default: 'Student Incidents'
            },
            rowData: Array,
            showHeader: {
                type: Boolean,
                default: true
            },
            showAddRowBtn: {
                type: Boolean,
                default: true
            },
        },

        data() {
            return {
                gridItems: [],
                totalCount: null,
                showIncidentEdit: false,
                showIncidentCreate: false,
                incidentId: null,
                detailCellRendererParams: null,
                detailRowHeight: null,
                // Infinite scroll configuration
                INFINITE_PAGE_SIZE: 100,
                serverTotalRows: null,
                lastSearchText: '',
                columnApi: null,
                gridApi: null,
                // Key to force grid re-render (incrementing this destroys and recreates the grid)
                gridKey: 0,
                // Store search text separately so it persists across grid recreations
                currentSearchText: '',
                // Store sort model separately so it persists across grid recreations
                currentSortModel: null,
            }
        },

        computed: {
            ...mapState('global', ['sessionUser', 'userPreferences']),

            // When Top Secret Mode is active we de-identify student and staff PII
            // in the Data List: the Student column and the staff name columns
            // (e.g. Observer) show ids instead of names. General free-text areas
            // (e.g. Description) are intentionally left untouched.
            isTopSecretModeActive() {
                return this.$isTopSecretModeActive(this.sessionUser, this.userPreferences)
            },

            urlParams() {
                return {
                    params: {user_id: this.sessionUser.user.id},
                    identifier: Date.now()
                }

            },

            useNewIncidentCreation() { return this.$store.getters['flags/flags']['ec-incident-repo-move'] === true },
            
            showAllIncidentCodes() { return this.$store.getters['flags/flags']['ec-all-incident-codes-2'] === true },
            
            gridOptions() {
                const me = this
                const baseOptions = {
                    defaultExportParams: {},
                    suppressContextMenu: true,
                    detailRowAutoHeight: true,
                    suppressScrollOnNewData: true,
                    rowBuffer: 20,
                    getRowHeight: (params) => {
                        if (params.node.detail) return null
                        return 50
                    },
                }
                
                // Add infinite scroll configuration for V3 API path.
                // ag-grid v25 infinite row model uses a fixed uniform row
                // height (from fe-grid's default rowHeight: 50); dynamic
                // per-row heights are not supported by this row model.
                if (this.showAllIncidentCodes) {
                    const { getRowHeight, ...infiniteBaseOptions } = baseOptions
                    return {
                        ...infiniteBaseOptions,
                        rowModelType: 'infinite',
                        datasource: this,
                        cacheBlockSize: this.INFINITE_PAGE_SIZE,
                        maxConcurrentDatasourceRequests: 1,
                        blockLoadDebounceMillis: 100,
                    }
                }
                
                return baseOptions
            },
            
            columns() {
                const me = this
                const baseCols = []
                
                // Initial expandable detail column only for old (primary incident only) format
                if (!this.showAllIncidentCodes) {
                    baseCols.push({
                        headerName: 'Details',
                        width: 76,
                        suppressSizeToFit: true,
                        onCellClicked: this.onCellClicked,
                        cellRendererFramework: 'cellIcon',
                        cellStyle: {
                            textAlign: 'center'
                        }
                    })
                }
                
                baseCols.push(
                    {
                        headerName: this.showAllIncidentCodes ? 'Date of Event' : 'Occurrence',
                        field: 'incident_date',
                        valueFormatter: params => {
                            if (!params.value) return ''
                            const date = dayjs(params.value)
                            return date.isValid() ? date.format('M/D/YYYY') : params.value.toString()
                        },
                        sortable: true,
                        minWidth: 110,
                        cellStyle: {
                            'vertical-align': 'top',
                            'align-items': 'flex-start'
                        }
                    },
                    {
                        headerName: 'Student',
                        field: 'student_full_name',
                        sortable: true,
                        minWidth: 130,
                        cellStyle: {
                            'vertical-align': 'top',
                            'align-items': 'flex-start'
                        }
                    },
                    {
                        headerName: this.showAllIncidentCodes ? 'School of Attendance' : 'Rostered School',
                        field: 'student_school_name',
                        sortable: true,
                        minWidth: 160,
                        cellStyle: {
                            'vertical-align': 'top',
                            'align-items': 'flex-start'
                        }
                    },
                    {
                        headerName: 'Grade',
                        field: 'grade_desc',
                        sortable: true,
                        minWidth: 70,
                        cellStyle: {
                            'vertical-align': 'top',
                            'align-items': 'flex-start'
                        }
                    }
                )
                
                if (this.showAllIncidentCodes) {
                    baseCols.push(
                        {
                            headerName: 'Description',
                            field: 'description',
                            sortable: true,
                            minWidth: 280,
                            maxWidth: 280,
                            resizable: false,
                            cellRendererFramework: 'descriptionCell',
                            cellStyle: {
                                'padding-top': '8px',
                                'align-items': 'flex-start'
                            },
                            suppressCellFlash: true,
                            suppressMovable: false,
                            onCellClicked: (params) => {
                                // Prevent row click from interfering with Show More link
                                const target = params.event.target
                                if (target && target.classList.contains('show-more-link')) {
                                    params.event.stopPropagation()
                                }
                            }
                        },
                        {
                            headerName: 'Incident Type & Behavior',
                            field: 'incident_types_codes',
                            cellRendererFramework: 'listPopoverCell',
                            sortable: true,
                            minWidth: 200,
                            cellStyle: {
                                'padding-top': '8px',
                                'align-items': 'flex-start'
                            }
                        },
                        {
                            headerName: 'Responses',
                            field: 'responses',
                            cellRendererFramework: 'listPopoverCell',
                            sortable: true,
                            minWidth: 200,
                            cellStyle: {
                                'padding-top': '8px',
                                'align-items': 'flex-start'
                            }
                        }
                    )
                } else {
                    baseCols.push(
                        {
                            headerName: 'Primary Incident Type',
                            field: 'incident_behavior_type_name',
                            sortable: true,
                            minWidth: 150
                        },
                        {
                            headerName: 'Primary Incident Behavior',
                            field: 'incident_behavior_name',
                            sortable: true,
                            minWidth: 170
                        },
                        {
                            headerName: 'Primary Response',
                            field: 'incident_response_name',
                            sortable: true,
                            minWidth: 150
                        }
                    )
                }
                
                // Common columns that are always shown
                baseCols.push(
                    {
                        headerName: 'Perceived Motivation',
                        field: 'incident_motivation_name',
                        sortable: true,
                        minWidth: 140
                    },
                    {
                        headerName: this.showAllIncidentCodes ? 'Observed by' : 'Observer',
                        field: 'observer_user_full_name',
                        sortable: true,
                        minWidth: 130
                    },
                    {
                        headerName: 'Location',
                        field: 'incident_location_name',
                        sortable: true,
                        minWidth: 120
                    },
                    {
                        headerName: 'School Year',
                        field: 'school_year_name',
                        sortable: true,
                        minWidth: 100,
                        hide: true
                    },
                    {
                        headerName: 'School',
                        field: 'school_name',
                        sortable: true,
                        minWidth: 120,
                        hide: true
                    },
                    {
                        headerName: 'District ID',
                        field: 'student_district_id',
                        sortable: true,
                        minWidth: 100,
                        hide: true
                    },
                    {
                        headerName: 'State ID',
                        field: 'student_state_id',
                        sortable: true,
                        minWidth: 100,
                        hide: true
                    },
                    {
                        headerName: 'Ethnicity',
                        field: 'student_ethnicity',
                        sortable: true,
                        minWidth: 100,
                        hide: true
                    },
                    {
                        headerName: 'Response Start Date & Time',
                        field: 'response_served_start_date',
                        sortable: true,
                        minWidth: 150,
                        hide: true,
                        valueFormatter: params => {
                            if (!params.value || params.value.startsWith('0000-00-00')) return ''
                            const date = dayjs(params.value)
                            return date.isValid() ? date.format('M/D/YYYY h:mm A') : params.value.toString()
                        }
                    },
                    {
                        headerName: 'Response End Date & Time',
                        field: 'response_served_end_date',
                        sortable: true,
                        minWidth: 150,
                        hide: true,
                        valueFormatter: params => {
                            if (!params.value || params.value.startsWith('0000-00-00')) return ''
                            const date = dayjs(params.value)
                            return date.isValid() ? date.format('M/D/YYYY h:mm A') : params.value.toString()
                        }
                    }
                )
                
                // Apply persisted sort state to column defs so the sort arrow
                // is restored when the grid is recreated via resetInfiniteScroll
                if (this.currentSortModel && this.currentSortModel.length > 0) {
                    baseCols.forEach(col => {
                        const sortInfo = this.currentSortModel.find(s => s.colId === col.field)
                        if (sortInfo) {
                            col.sort = sortInfo.sort
                            col.sortIndex = 0
                        }
                    })
                }

                return baseCols
            }
        },

        watch: {
            params: {
                deep: true,
                handler() {
                    this.gridItems = []
                    this.loadData()
                }
            },
        },

        beforeMount() {
            // Set up detail cell renderer for old format (master-detail)
            this.detailCellRendererParams = {
                template: (v) => {
                    let val = '<div ref="eDetailGrid"><div style="height: 100%; padding: 10px; line-height: 22px;">' +
                        '<div ref="incident-incidents" class="incident-grid-description pa-2">' +
                        '<span style="font-weight: 700; font-size: 14px;">Incidents </span>'
                        if(v.data?.behaviors?.length) {
                            v.data.behaviors.forEach(behavior => {
                                val += '<div style="font-size: 12px;">' + behavior.display_name 
                            if(behavior.primary_flag) {
                                val += ' <span class="incident-grid-primary-chip" aria-label="Primary behavior">Primary</span>'
                            }
                                val += '</div>' 
                            })
                        }
                        val += '</div>'
                        if(v.data?.description) {
                            val += '<div ref="incident-desc" class="incident-grid-description pa-2">' +
                                '<span style="font-weight: 700; font-size: 14px;">Description </span>' + 
                                '<div style="font-size: 12px; white-space: normal;">' + v.data.description + '</div>' + 
                            '</div>'
                        }
                        if(v.data?.responses?.length) {
                            val += '<div ref="incident-responses" class="incident-grid-description pa-2">' +
                                '<span style="font-weight: 700; font-size: 14px;">Responses </span>'
                            v.data.responses.forEach(response => {
                                val += '<div style="font-size: 12px;">' + response.incident_response_name
                            if(response.primary_flag) {
                                val += ' <span class="incident-grid-primary-chip" aria-label="Primary response">Primary</span>'
                            }
                                val += '</div>' 
                            })
                        val += '</div>'
                        }
                        val += '</div></div>'
                    return val
                },
                detailGridOptions: {
                    columnDefs: [
                        {
                            headerName: 'Incident Type',
                            field: 'incident_behavior_display_name',
                            sortable: true,
                            minWidth: 150,
                        },
                        {
                            headerName: 'Incident Behavior',
                            field: 'incident_behavior_name',
                            sortable: true,
                            minWidth: 170,
                        },
                        {
                            headerName: 'Response',
                            field: 'incident_response_name',
                            sortable: true,
                            minWidth: 150,
                        }
                    ],
                    suppressContextMenu: true,
                    defaultColDef: {
                        cellStyle: {
                            'white-space': 'normal',
                            'margin': 'auto',
                        }
                    },
                },
            }
        },

        created() {
            window.addEventListener('beforeunload', this.editHeaders)
        },


        mounted() {
            this.loadData()
            this.$nextTick(() => {
                this.addToolbarDataTests()
            })
        },

        methods: {
            addToolbarDataTests() {
                const downloadBtn = this.$el && this.$el.querySelector('.fe-grid-toolbar-btn .fa-arrow-alt-to-bottom')?.closest('.fe-grid-toolbar-btn')
                if (downloadBtn) {
                    downloadBtn.setAttribute('data-test', 'incidents-datalist-download-btn')
                }
                const settingsBtn = this.$el && this.$el.querySelector('.fe-grid-toolbar-btn .fa-cog')?.closest('.fe-grid-toolbar-btn')
                if (settingsBtn) {
                    settingsBtn.setAttribute('data-test', 'incidents-datalist-settings-btn')
                }
                const searchInput = this.$el && this.$el.querySelector('.fe-grid-filter-field input')
                if (searchInput) {
                    searchInput.setAttribute('data-test', 'incidents-datalist-search')
                    // For server-side search (V3 API), indicate the 3 character minimum
                    if (this.showAllIncidentCodes) {
                        searchInput.setAttribute('placeholder', 'Search (3 char min)')
                    }
                }
            },
            /**
             * Transform V3 array-based API response to array of objects.
             * The V3 response has columns metadata and incidents as arrays of values.
             */
            transformV3ArrayToObjects(response) {
                const columns = response.columns || []
                const behaviorsColumns = response.behaviors_columns || []
                const responsesColumns = response.responses_columns || []
                const incidents = response.incidents || []
                
                // Find indices for behaviors and responses in the columns array
                const behaviorsIdx = columns.indexOf('behaviors')
                const responsesIdx = columns.indexOf('responses')
                
                return incidents.map(row => {
                    // Convert array row to object using column names
                    const obj = {}
                    columns.forEach((col, idx) => {
                        obj[col] = row[idx]
                    })
                    
                    // Transform behaviors array of arrays to array of objects
                    if (behaviorsIdx !== -1 && Array.isArray(row[behaviorsIdx])) {
                        obj.behaviors = row[behaviorsIdx].map(behaviorRow => {
                            const behaviorObj = {}
                            behaviorsColumns.forEach((col, idx) => {
                                behaviorObj[col] = behaviorRow[idx]
                            })
                            return behaviorObj
                        })
                    }
                    
                    // Transform responses array of arrays to array of objects
                    if (responsesIdx !== -1 && Array.isArray(row[responsesIdx])) {
                        obj.responses = row[responsesIdx].map(responseRow => {
                            const responseObj = {}
                            responsesColumns.forEach((col, idx) => {
                                responseObj[col] = responseRow[idx]
                            })
                            return responseObj
                        })
                    }
                    
                    return obj
                })
            },
            
            /**
             * Top Secret Mode: replace names with ids so the grid and CSV export
             * expose no PII. student_full_name -> student id; every
             * *_user_full_name -> its *_user_id. A free-text observer has no
             * observer_user_id, so it blanks out (hiding that free-text PII).
             */
            applyTopSecretMasking(rows) {
                if (!this.isTopSecretModeActive || !Array.isArray(rows)) return rows
                return rows.map(row => {
                    const masked = { ...row }
                    if ('student_full_name' in masked) {
                        masked.student_full_name = String(masked.student_id ?? masked.id ?? '')
                    }
                    Object.keys(masked).forEach(key => {
                        const staffField = key.match(/^(.+)_user_full_name$/)
                        if (staffField) {
                            const userId = masked[`${staffField[1]}_user_id`]
                            masked[key] = userId ? String(userId) : ''
                        }
                    })
                    return masked
                })
            },

            transformV2Data(incidents) {
                const transformed = incidents.map(incident => {
                    // Keep behaviors array for rendering with primary badges
                    let behaviorsArray = []
                    if (incident.behaviors && incident.behaviors.length > 0) {
                        behaviorsArray = incident.behaviors
                    } else if (incident.incident_behavior_display_name) {
                        // Fallback to primary if no behaviors array (backward compatibility)
                        behaviorsArray = [{
                            display_name: incident.incident_behavior_display_name,
                            primary_flag: true
                        }]
                    }
                    
                    // Keep responses array for rendering with primary badges
                    let responsesArray = []
                    if (incident.responses && incident.responses.length > 0) {
                        responsesArray = incident.responses
                    } else if (incident.incident_response_name) {
                        // Fallback to primary if no responses array (backward compatibility)
                        responsesArray = [{
                            incident_response_name: incident.incident_response_name,
                            primary_flag: true
                        }]
                    }
                    
                    // Derive incident_date from incident_datetime for the Date of Event column
                    const incidentDate = incident.incident_date
                        || (incident.incident_datetime ? incident.incident_datetime.split(' ')[0] : null)

                    return {
                        ...incident,
                        incident_date: incidentDate,
                        _behaviorsArray: behaviorsArray,
                        _responsesArray: responsesArray,
                        incident_types_codes: behaviorsArray.map(b => b.display_name).join('\n'),
                        responses: responsesArray.map(r => r.incident_response_name).join('\n'),
                    }
                })

                return this.applyTopSecretMasking(transformed)
            },

            loadData() {
                if (this.rowData) {
                    // If rowData is provided, check if it needs transformation
                    this.gridItems = this.showAllIncidentCodes && this.rowData[0]?.behaviors
                        ? this.transformV2Data(this.rowData)
                        : this.applyTopSecretMasking(this.rowData)
                } else if (this.showAllIncidentCodes) {
                    // V3 path: Use infinite scroll - data is loaded via getRows datasource method
                    // The datasource is set directly in gridOptions, so nothing to do here on initial load
                    // For subsequent reloads (e.g., after incident update), reset the datasource to refresh data
                    // Only reset if grid is already initialized (gridApi is set in onGridReady)
                    if (this.gridApi) {
                        this.resetInfiniteScroll()
                    }
                    // If gridApi is not set, the initial load will happen automatically via datasource in gridOptions
                } else {
                    // Legacy path: Load all data at once
                    this.$setLoading(true)
                    this.$modelGet('studentIncidentList', this.params)
                        .then(response => {
                            this.gridItems = this.applyTopSecretMasking(response.map(r => ({
                                ...r,
                                incident_date: r.incident_date || (r.incident_datetime ? r.incident_datetime.split(' ')[0] : null)
                            })))
                            this.$setLoading(false)
                        })
                        .catch(error => {
                            console.error('Error loading incidents:', error)
                            this.$setLoading(false)
                        })
                }
            },
            
            /**
             * ag-grid infinite scroll datasource method.
             * Called by ag-grid when it needs more rows.
             */
            getRows(params) {
                // Use stored search text (since grid might be recreated and fe-grid search input cleared)
                const searchText = this.currentSearchText || ''
                
                // Only search if 3+ characters (or empty to show all)
                const effectiveSearch = searchText.length >= 3 ? searchText : ''
                
                // Build API params
                let apiParams = {
                    ...this.params,
                    start_row: params.startRow,
                    end_row: params.endRow,
                }
                
                // Add search if present
                if (effectiveSearch) {
                    apiParams.search = effectiveSearch
                }
                
                // Add sorting if present (use persisted sort model if params.sortModel is empty after grid recreation)
                const effectiveSortModel = (params.sortModel && params.sortModel.length > 0) 
                    ? params.sortModel 
                    : this.currentSortModel
                if (effectiveSortModel && effectiveSortModel.length > 0) {
                    apiParams.sort = effectiveSortModel[0].colId
                    apiParams.dir = effectiveSortModel[0].sort
                }
                
                this.$modelGet('incidentBehaviorListV3', apiParams)
                    .then(response => {
                        // Transform V3 array format to objects
                        const incidents = this.transformV3ArrayToObjects(response)
                        const transformedData = this.transformV2Data(incidents)
                        
                        // Get total count from pagination metadata
                        const total = response.pagination?.total ?? transformedData.length
                        this.serverTotalRows = total
                        this.totalCount = total
                        
                        // Calculate lastRow for ag-grid infinite scroll:
                        // - If we got fewer rows than requested, we've reached the end
                        // - Otherwise, use the total from the server
                        const requestedCount = params.endRow - params.startRow
                        const returnedCount = transformedData.length
                        let lastRow
                        
                        if (returnedCount < requestedCount) {
                            // We're at the end - got fewer rows than requested
                            lastRow = params.startRow + returnedCount
                        } else if (params.startRow + returnedCount >= total) {
                            // We've loaded up to or past the total
                            lastRow = total
                        } else {
                            // More data available - use total so ag-grid knows the full extent
                            lastRow = total
                        }
                        
                        params.successCallback(transformedData, lastRow)
                        
                        // ag-grid v25 infinite row model uses a fixed uniform
                        // rowHeight for all rows (dynamic heights unsupported).
                        // Multi-item content is handled via truncation + popover
                        // in the cell renderers instead.
                    })
                    .catch(error => {
                        console.error('Error loading incidents:', JSON.stringify(error))
                        params.failCallback()
                    })
            },
            
            /**
             * Reset the infinite scroll grid by forcing a complete re-render.
             * This recreates the grid from scratch, ensuring proper row count reset.
             */
            resetInfiniteScroll() {
                // Incrementing the key forces Vue to destroy and recreate the entire grid
                // This ensures ag-grid starts fresh with correct row counts
                this.gridKey++
                // Reset gridApi since the grid will be recreated
                this.gridApi = null
            },
            
            /**
             * Handle search input from fe-grid's built-in search field.
             * Resets the datasource to reload data with the new search term.
             * 
             * Debounce: 500ms balances user responsiveness with API load reduction.
             * This matches the pattern used in other infinite scroll grids (e.g., StudentList.vue).
             */
            onServerSearch: lodash.debounce(function() {
                if (!this.showAllIncidentCodes) return
                
                const searchText = this.$refs.grid?.filterText || ''
                
                // Only trigger server search if search is empty or has 3+ characters
                if (searchText.length > 0 && searchText.length < 3) {
                    return
                }
                
                // Avoid duplicate searches for the same text
                if (searchText === this.lastSearchText) {
                    return
                }
                this.lastSearchText = searchText
                
                // Store search text before grid recreation (since fe-grid search input will be cleared)
                this.currentSearchText = searchText
                
                // Reset grid to reload from the beginning with new search
                this.resetInfiniteScroll()
            }, 500),
            
            /**
             * Handle sort changes from fe-grid.
             * Captures the sort state then recreates the grid. The sort state
             * is restored via the columns computed property which reads
             * currentSortModel and sets sort/sortIndex on the matching column def.
             */
            onSortChanged() {
                if (!this.showAllIncidentCodes) return
                
                const columnApi = this.columnApi || this.$refs.grid?.gridOptions?.columnApi
                if (columnApi) {
                    const columnState = columnApi.getColumnState() || []
                    const sortedColumns = columnState.filter(col => col.sort !== null)
                    this.currentSortModel = sortedColumns.length > 0 
                        ? sortedColumns.map(col => ({ colId: col.colId, sort: col.sort }))
                        : null
                }
                
                this.resetInfiniteScroll()
            },

            onCellClicked(rowNode) {
                let api = this.$refs.grid.gridOptions.api;
                api.getDisplayedRowAtIndex(rowNode.node.rowIndex).setExpanded(!rowNode.node.expanded)
                api.refreshCells({force: true})
            },

            loadIncident(meta) {
                if(this.useNewIncidentCreation) {
                    this.$store.commit('global/addDockableWindow', {
                        name: 'Edit Incident',
                        component: 'incident-creation',
                        attrs: {
                            params: {
                                user_id: this.sessionUser.user.id,
                                incident_id: meta.data.incident_id
                            },
                            identifier: new Date().getMilliseconds()
                        }
                    })
                } else {
                    const identifier = new Date().getMilliseconds()
                    let win = {
                        name: 'Edit Incident',
                        component: 'i-frame-renderer',
                        attrs: {
                            app: 'incident_creation',
                            cfg: {
                                params: {
                                    user_id: this.sessionUser.user.id,
                                    incident_id: meta.data.incident_id
                                },
                                identifier
                            }
                        },
                    }

                    // The incident entry form (rendered inside IFrameRenderer's iframe) posts
                    // {identifier, action: 'close'} back via window.parent.postMessage when the user closes it.
                    const onMessage = (e) => {
                        if (!e.origin.endsWith('.educlimber.dev') && !e.origin.endsWith('.educlimber.app') && !e.origin.endsWith('.educlimber.com') && e.origin !== window.location.origin) {
                            return
                        }

                        let eventData
                        try {
                            eventData = (typeof e.data === 'string' || e.data instanceof String) ? JSON.parse(e.data) : e.data
                        } catch (err) {
                            return
                        }

                        // loose equality: identifier round-trips through the iframe's URL query string,
                        // so it comes back as a string even though it was set here as a number
                        if (eventData?.identifier == identifier && eventData?.action === 'close') {
                            window.removeEventListener('message', onMessage)
                            this.loadData()
                            this.$store.commit('global/removeDockedWindow', win)
                            this.$snackbars.$emit('new', {text: 'Successfully deleted incident', usage: 'success'})
                        }
                    }
                    window.addEventListener('message', onMessage)

                    // Closing the window via its own close control (rather than the
                    // iframe's postMessage) never runs the branch above, so clean up here too.
                    win.events = {
                        close: () => window.removeEventListener('message', onMessage)
                    }

                    this.$store.commit('global/addDockableWindow', win)
                }
            },

            createIncident() {
                if(this.useNewIncidentCreation) {
                    this.$store.commit('global/addDockableWindow', {
                        name: 'Create Incident',
                        component: 'incident-creation',
                        attrs: {}
                    })
                } else {
                    this.$store.commit('global/addDockableWindow', {
                        name: 'Create Incident',
                        component: 'i-frame-renderer',
                        attrs: {
                            app: 'incident_creation'
                        }
                    })
                }
            },

            incidentUpdate(type) {
                this.$emit('update')
                this.loadData()
                if(type === 'edit') {
                    this.showIncidentEdit = false
                } else {
                    this.showIncidentCreate = false
                }
            },

            resize() {
                this.$refs.grid.resize()
            },

            calculateDetailRowHeight(params) {
                // added timeout here since it was trying to find the class before
                // the element was in the DOM (e.g. when user clicks open)
                setTimeout(function () {
                    let height = 50
                    if (params.node && params.node.detail && document.querySelectorAll(".incident-grid-description").length > 0) {
                        // offset makes single line description height have no scroll
                        height = document.querySelectorAll(".incident-grid-description")[0].offsetHeight + 18
                    }
                    params.node.setRowHeight(height)
                    this.$refs.grid.$refs.grid.gridOptions.api.onRowHeightChanged()
                }.bind(this), 100)
            },

            csvExportProcessCellCallback(params) {
                if (params.value === undefined) {
                    if (params.node?.data?.description) {
                        return params.node.data.description
                    }
                }
                
                // Add "(Primary)" suffix to primary values in CSV export
                const field = params.column?.getColDef()?.field
                const data = params.node?.data
                
                if ((field === 'incident_types_codes' || field === 'responses') && data) {
                    // For incident types, use _behaviorsArray or behaviors array
                    if (field === 'incident_types_codes') {
                        const array = data._behaviorsArray || data.behaviors
                        if (array && Array.isArray(array)) {
                            const result = array
                                .filter(item => item != null && item.display_name) // Remove null/undefined items and items without display_name
                                .map(item => {
                                    const name = String(item.display_name).trim()
                                    if (!name) return null
                                    return item.primary_flag ? `${name} (Primary)` : name
                                })
                                .filter(item => item !== null && item !== '') // Filter out nulls from map and empty strings
                            return result.length > 0 ? result.join('\n') : ''
                        }
                    }
                    
                    // For responses, use _responsesArray (contains primary_flag) or fallback to responses array
                    if (field === 'responses') {
                        const responsesArray = data._responsesArray || data.responses
                        if (responsesArray && Array.isArray(responsesArray)) {
                            const result = responsesArray
                                .filter(item => item != null && item.incident_response_name) // Remove null/undefined items and items without incident_response_name
                                .map(item => {
                                    const name = String(item.incident_response_name).trim()
                                    if (!name) return null
                                    return item.primary_flag ? `${name} (Primary)` : name
                                })
                                .filter(item => item !== null && item !== '') // Filter out nulls from map and empty strings
                            return result.length > 0 ? result.join('\n') : ''
                        }
                    }
                }
                
                // Handle date fields - format properly or return empty for null dates
                if (field === 'response_served_start_date' || field === 'response_served_end_date') {
                    if (!params.value || params.value === '0000-00-00 00:00:00') return ''
                    const date = dayjs(params.value)
                    return date.isValid() ? date.format('M/D/YYYY h:mm A') : ''
                }
                
                // Trim any leading/trailing whitespace from the value
                return typeof params.value === 'string' ? params.value.trim() : params.value
            },
            
            /**
             * Handle CSV export for the grid.
             * For infinite scroll mode (V3), fetches all data before exporting.
             * For legacy mode, uses ag-grid's built-in export.
             *
             * fe-grid treats csvExportProcessCallback as a full replacement
             * for the default export (it does not check the return value),
             * so both paths must trigger the export themselves.
             */
            handleExport(params) {
                if (!this.showAllIncidentCodes) {
                    if (this.gridApi) {
                        this.gridApi.exportDataAsCsv(params)
                    }
                    return
                }
                
                // For V3 infinite scroll mode, fetch all data and export
                this.$setLoading(true)
                
                // Build API params without pagination to get all records
                let apiParams = {
                    ...this.params,
                }
                
                // Add current search if present
                if (this.currentSearchText) {
                    apiParams.search = this.currentSearchText
                }
                
                // Fetch all data (no pagination params = all records)
                this.$modelGet('incidentBehaviorListV3', apiParams)
                    .then(response => {
                        // Transform V3 array format to objects
                        const incidents = this.transformV3ArrayToObjects(response)
                        const transformedData = this.transformV2Data(incidents)
                        
                        // Generate CSV content
                        this.generateAndDownloadCsv(transformedData, params)
                        this.$setLoading(false)
                    })
                    .catch(error => {
                        console.error('Error exporting incidents:', JSON.stringify(error))
                        this.$setLoading(false)
                        this.$snackbars.$emit('new', { text: 'Error exporting data. Please try again.', usage: 'error' })
                    })
            },
            
            /**
             * Generate CSV content and trigger download.
             */
            generateAndDownloadCsv(data, exportParams) {
                // Get visible columns from the grid
                if (!this.columnApi) {
                    console.error('Cannot export: columnApi is not available')
                    this.$snackbars.$emit('new', { text: 'Error exporting data. Please try again.', usage: 'error' })
                    return
                }
                
                const displayedColumns = this.columnApi?.getAllDisplayedColumns() || []
                const visibleColumns = displayedColumns
                    .map(col => col?.getColDef())
                    .filter(colDef => colDef?.field) // Only columns with field property
                
                // Build header row
                const headers = visibleColumns.map(col => col.headerName || col.field)
                
                // Build data rows
                const rows = data.map(row => {
                    return visibleColumns.map(col => {
                        const field = col.field
                        let value = row[field]
                        
                        // Handle special fields using same logic as csvExportProcessCellCallback
                        if (field === 'incident_types_codes') {
                            const array = row._behaviorsArray || row.behaviors
                            if (array && Array.isArray(array)) {
                                value = array
                                    .filter(item => item != null && item.display_name)
                                    .map(item => {
                                        const name = String(item.display_name).trim()
                                        if (!name) return null
                                        return item.primary_flag ? `${name} (Primary)` : name
                                    })
                                    .filter(item => item !== null && item !== '')
                                    .join('\n')
                            }
                        } else if (field === 'responses') {
                            const responsesArray = row._responsesArray || row.responses
                            if (responsesArray && Array.isArray(responsesArray)) {
                                value = responsesArray
                                    .filter(item => item != null && item.incident_response_name)
                                    .map(item => {
                                        const name = String(item.incident_response_name).trim()
                                        if (!name) return null
                                        return item.primary_flag ? `${name} (Primary)` : name
                                    })
                                    .filter(item => item !== null && item !== '')
                                    .join('\n')
                            }
                        } else if (field === 'response_served_start_date' || field === 'response_served_end_date') {
                            if (!value || value === '0000-00-00 00:00:00') {
                                value = ''
                            } else {
                                const date = dayjs(value)
                                value = date.isValid() ? date.format('M/D/YYYY h:mm A') : ''
                            }
                        }
                        
                        // Convert to string and escape for CSV
                        if (value === null || value === undefined) {
                            return ''
                        }
                        value = String(value).trim()
                        // Escape quotes and wrap in quotes if contains comma, quote, or newline
                        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                            value = '"' + value.replace(/"/g, '""') + '"'
                        }
                        return value
                    }).join(',')
                })
                
                // Combine headers and rows
                const csvContent = [headers.join(','), ...rows].join('\n')
                
                // Create and trigger download
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
                const link = document.createElement('a')
                const url = URL.createObjectURL(blob)
                link.setAttribute('href', url)
                link.setAttribute('download', exportParams?.fileName || 'incidents_export.csv')
                link.style.visibility = 'hidden'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)
            },

            updateIncidentCount() {
                this.totalCount = this.$refs?.grid?.displayedRowCount()
            },

            onGridReady(grid) {
                let me = this
                this.columnApi = grid.columnApi
                this.gridApi = grid.api
                
                // Restore search text to fe-grid after grid recreation (for infinite scroll mode)
                if (this.showAllIncidentCodes && this.currentSearchText) {
                    // Use nextTick to ensure fe-grid is fully initialized
                    this.$nextTick(() => {
                        if (this.$refs.grid) {
                            this.$refs.grid.filterText = this.currentSearchText
                        }
                    })
                }
                
                // Re-apply toolbar customizations after grid recreation
                this.$nextTick(() => {
                    this.addToolbarDataTests()
                })
                
                let previousHeaders = window.localStorage.getItem('ec-incident-big-five-headers') || '[]'
                previousHeaders = JSON.parse(previousHeaders)
                if(previousHeaders.length) {
                    let hasUpdates = false
                    previousHeaders.forEach(head => {
                        const column = this.columns.find(x => x.field == head.field)
                        if (column) {
                            column.hide = head.hide
                            hasUpdates = true
                        }
                    })
                    if (hasUpdates) {
                        grid.api.setColumnDefs(this.columns)
                    }
                }
                
                grid.api.sizeColumnsToFit()
                
                // add global listener to set local storage on visibility change for Ethnicity column
                // so that going from datawall -> charts -> datawall it remains the same
                grid.api.addGlobalListener(function(type, event) {
                    if(type.indexOf("column") >= 0) {
                        if(event.type == "columnVisible") {
                            // Recalculate row heights when column visibility changes
                            grid.api.resetRowHeights()
                            
                            if (event.column.colId == "student_ethnicity") {
                                me.columns.forEach(head => {
                                    head.hide = !me.columnApi.getAllColumns().find(col => col.colDef.field == head.field).visible
                                })
                                window.localStorage.setItem('ec-incident-big-five-headers', JSON.stringify(me.columns))
                            }
                        }
                    }
                });
            },

            editHeaders() {
                this.columns.forEach(head => {
                    head.hide = !this.columnApi.getAllColumns().find(col => col.colDef.field == head.field).visible
                })
                window.localStorage.setItem('ec-incident-big-five-headers', JSON.stringify(this.columns))
            }
        }
    }
</script>

<style lang="scss">
    .incident-grid-primary-chip {
        background: #0EA449; //#4CAF50;
        color: #fff;
        border-radius: 16px;
        padding: 3px 6px 3px 6px;
        height: 16px;
        width: 48px;
        margin-left: 8px;
        font-size: 11px
    }
    
    .incident-primary-badge {
        background: #ECEDF1;
        color: #050F2D;
        border-radius: 16px;
        padding: 0px 7px;
        font-size: 10px;
        line-height: 1.7;
        flex-shrink: 0;
        white-space: nowrap;
    }
    
    .ec-description-popover {
        position: fixed;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        padding: 12px;
        z-index: 1000;
        max-width: 400px;
        box-sizing: border-box;
        
        .ec-description-popover-content {
            word-wrap: break-word;
            word-break: normal;
            overflow-wrap: break-word;
            white-space: normal;
            line-height: 1.5;
            color: #333;
            font-family: 'CerebriSans-Regular', Cerebri Sans, 'Roboto', sans-serif;
        }
        
        .ec-description-popover-close {
            display: block;
            margin-top: 8px;
            color: #006C90;
            text-decoration: none;
            cursor: pointer;
            line-height: 20px;
            font-family: 'CerebriSans-Regular', Cerebri Sans, 'Roboto', sans-serif;
            
            &:hover {
                color: #005a7a;
                text-decoration: underline;
            }
        }
    }
</style>

<style lang="scss" scoped>
    .incident-grid {
        ::v-deep .ag-details-row {
            padding: 0 !important;
        }

        ::v-deep .ag-details-grid {
            .ag-header {
                display: none !important;
            }

            .ag-cell {
                font-family: 'HyperlegibleSans-Regular', Hyperlegible Sans, 'Roboto', sans-serif !important;
            }
        }

        ::v-deep [col-id="description"] {
            .description-cell-wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;
                box-sizing: border-box;

                .description-text {
                    display: inline;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    line-height: 20px;
                    max-width: 100%;
                }

                .show-more-link {
                    display: inline-block;
                    color: #006C90;
                    text-decoration: none;
                    cursor: pointer;
                    line-height: 20px;

                    &:hover {
                        color: #005a7a;
                        text-decoration: underline;
                    }
                }
            }
        }

        ::v-deep [col-id="incident_types_codes"],
        ::v-deep [col-id="responses"] {
            .incident-list-cell-wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;

                .incident-cell-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    line-height: 22px;
                    min-width: 0;

                    .incident-cell-text {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        min-width: 0;
                    }
                }

                .show-more-link {
                    color: #006C90;
                    text-decoration: none;
                    cursor: pointer;
                    line-height: 20px;

                    &:hover {
                        color: #005a7a;
                        text-decoration: underline;
                    }
                }
            }
        }
    }

    .incident-count-display {
        display: flex;
        align-items: center;
        padding: 0 16px;
    }

    .incident-count-text {
        font-size: 14px;
        font-weight: 600;
    }
</style>
